import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { dashboardApi, serialNumbersApi, praxisPoolApi, exchangeApi } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Users, Hash, FileText, Plus, ArrowRight, Clock, Bell, Phone, AlertTriangle,
  Sun, Target, UserPlus, CalendarClock, Check, Activity, Pencil, Trash2, RotateCcw, ListTodo,
} from "lucide-react";
import { UpcomingEventsNotification } from "@/components/UpcomingEventsNotification";
import { useToast } from "@/components/SimpleToast";

function KpiCard({ to, title, value, subtext, icon: Icon, tone = "primary", loading, testid }) {
  const tones = {
    primary: "text-primary bg-primary/10",
    warning: "text-warning bg-warning/10",
    info: "text-info bg-info/10",
    success: "text-success bg-success/10",
  };
  return (
    <Link to={to} className="group" data-testid={testid}>
      <div className="relative overflow-hidden rounded-xl border border-border bg-card p-5 sm:p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all h-full">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            {loading ? (
              <div className="h-9 w-16 mt-2 rounded-md bg-muted animate-pulse" />
            ) : (
              <p className="text-3xl font-bold mt-2 tracking-tight tabular-nums" data-testid={`${testid}-value`}>{value}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">{subtext}</p>
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tones[tone]}`}>
            <Icon className="w-5 h-5" strokeWidth={1.75} aria-hidden="true" />
          </div>
        </div>
      </div>
    </Link>
  );
}

const ACTION_META = {
  create: { icon: Plus, label: "erstellt", tone: "text-success bg-success/10" },
  created: { icon: Plus, label: "erstellt", tone: "text-success bg-success/10" },
  update: { icon: Pencil, label: "geändert", tone: "text-info bg-info/10" },
  updated: { icon: Pencil, label: "geändert", tone: "text-info bg-info/10" },
  delete: { icon: Trash2, label: "gelöscht", tone: "text-destructive bg-destructive/10" },
  deleted: { icon: Trash2, label: "gelöscht", tone: "text-destructive bg-destructive/10" },
  restore: { icon: RotateCcw, label: "wiederhergestellt", tone: "text-primary bg-primary/10" },
  restored: { icon: RotateCcw, label: "wiederhergestellt", tone: "text-primary bg-primary/10" },
};
const ENTITY_LABEL = {
  customer: "Kunde", service_report: "Servicebericht", serial_number: "Seriennummer",
  task: "Aufgabe", lead: "Lead",
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "gerade eben";
  const m = Math.floor(s / 60);
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  const dd = Math.floor(h / 24);
  if (dd < 7) return `vor ${dd} Tag${dd > 1 ? "en" : ""}`;
  return d.toLocaleDateString("de-DE");
};

function ActivityFeed({ items }) {
  return (
    <Card data-testid="dashboard-activity-feed" className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="w-5 h-5 text-primary" />
          Aktivitäts-Feed
        </CardTitle>
        <CardDescription>Letzte Änderungen im System</CardDescription>
      </CardHeader>
      <CardContent>
        {items.length > 0 ? (
          <div className="space-y-4">
            {items.map((log) => {
              const meta = ACTION_META[log.action] || { icon: Activity, label: log.action || "Aktivität", tone: "text-muted-foreground bg-muted" };
              const Icon = meta.icon;
              const text = log.summary
                || `${ENTITY_LABEL[log.entity_type] || log.entity_type || "Eintrag"} ${meta.label}${log.entity_name ? ` · ${log.entity_name}` : ""}`;
              return (
                <div key={log.id} className="flex gap-3" data-testid={`activity-item-${log.id}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.tone}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-foreground leading-snug">
                      <span className="font-medium">{log.actor_name || "System"}</span>{" "}
                      <span className="text-muted-foreground">{text}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{timeAgo(log.created_at)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state py-8" data-testid="dashboard-activity-empty">
            <Activity className="w-10 h-10 text-muted-foreground/40" />
            <p className="text-muted-foreground mt-3 text-sm">Noch keine Aktivitäten erfasst</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const toast = useToast();
  const [stats, setStats] = useState(null);
  const [expiringSerials, setExpiringSerials] = useState([]);
  const [myDay, setMyDay] = useState({ total: 0, done: 0 });
  const [kpis, setKpis] = useState({ openLeads: 0, overdueTasks: 0 });
  const [followups, setFollowups] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, expiringRes, myDayRes, leadsRes, tasksRes, activityRes] = await Promise.all([
        dashboardApi.getStats(),
        serialNumbersApi.getExpiring(),
        api.get("/my-day").catch(() => ({ data: { total: 0, done: 0 } })),
        praxisPoolApi.summary().catch(() => ({ data: [] })),
        api.get("/tasks").catch(() => ({ data: [] })),
        dashboardApi.getActivity().catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setExpiringSerials(Array.isArray(expiringRes.data) ? expiringRes.data : []);
      setMyDay({ total: myDayRes.data?.total || 0, done: myDayRes.data?.done || 0 });
      setActivity(Array.isArray(activityRes.data) ? activityRes.data : []);

      const today = new Date().toISOString().split("T")[0];
      const leads = Array.isArray(leadsRes.data) ? leadsRes.data : [];
      const openLeads = leads.reduce((sum, s) => sum + (s.new_count || 0), 0);
      const tasks = Array.isArray(tasksRes.data) ? tasksRes.data : [];
      const overdueTasks = tasks.filter(
        (t) => t.status !== "Erledigt" && t.due_date && String(t.due_date).slice(0, 10) < today
      ).length;
      setKpis({ openLeads, overdueTasks });

      const fups = tasks
        .filter((t) => t.is_followup && t.status !== "Erledigt")
        .sort((a, b) => {
          const da = a.due_date ? String(a.due_date).slice(0, 10) : "9999-12-31";
          const dbb = b.due_date ? String(b.due_date).slice(0, 10) : "9999-12-31";
          return da.localeCompare(dbb);
        });
      setFollowups(fups);
    } catch (error) {
      console.debug("Dashboard-Daten konnten nicht geladen werden:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const markFollowupDone = async (taskId) => {
    setFollowups((f) => f.filter((t) => t.id !== taskId));
    try {
      await api.put(`/tasks/${taskId}`, { status: "Erledigt" });
      toast.success("Wiedervorlage erledigt");
      window.dispatchEvent(new Event("tasks-changed"));
    } catch {
      toast.error("Konnte nicht als erledigt markiert werden");
      fetchData();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="spinner" />
      </div>
    );
  }

  const customersCount = stats?.customers_count || 0;
  const kundeCount = stats?.kunde_count || 0;
  const interessentCount = stats?.interessent_count || 0;
  const reportsCount = stats?.reports_count || 0;
  const recentReports = Array.isArray(stats?.recent_reports) ? stats.recent_reports : [];
  const today = new Date().toISOString().split("T")[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try { return new Date(dateStr).toLocaleDateString("de-DE"); } catch { return dateStr; }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Hero + Schnellaktionen */}
      <div className="brand-hero rounded-2xl p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Übersicht</h1>
          <p className="text-muted-foreground mt-1">Willkommen zurück! Hier ist Ihr Überblick.</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="dashboard-quick-actions">
          <Button asChild className="shadow-sm" data-testid="quick-add-customer-btn">
            <Link to="/customers?new=1"><UserPlus className="w-4 h-4 mr-2" />Neuer Kontakt</Link>
          </Button>
          <Button asChild variant="outline" className="bg-card/60" data-testid="quick-add-report-btn">
            <Link to="/service-reports/new"><FileText className="w-4 h-4 mr-2" />Neuer Servicebericht</Link>
          </Button>
          <Button asChild variant="outline" className="bg-card/60" data-testid="quick-add-task-btn">
            <Link to="/tasks?new=1"><ListTodo className="w-4 h-4 mr-2" />Neue Aufgabe</Link>
          </Button>
        </div>
      </div>

      {/* KPI-Kacheln */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5" data-testid="dashboard-kpis">
        <KpiCard to="/customers?category=Kunde" title="Kunden" value={kundeCount} subtext={`${customersCount} Einträge gesamt`} icon={Users} tone="success" testid="kpi-customers" loading={loading} />
        <KpiCard to="/customers?category=Interessent" title="Interessenten" value={interessentCount} subtext="Potenzielle Neukunden" icon={UserPlus} tone="info" testid="kpi-interessenten" loading={loading} />
        <KpiCard to="/praxissuche" title="Offene Leads" value={kpis.openLeads} subtext="Neue, unbearbeitete Leads" icon={Target} tone="primary" testid="kpi-open-leads" loading={loading} />
        <KpiCard to="/tasks?overdue=1" title="Überfällige Aufgaben" value={kpis.overdueTasks} subtext="Frist überschritten" icon={AlertTriangle} tone="warning" testid="kpi-overdue-tasks" loading={loading} />
      </div>

      {/* Hauptraster: Inhalt + Aktivitäts-Feed */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Linke Spalte */}
        <div className="xl:col-span-2 space-y-6">
          {/* Garantie/Wartung-Alarm */}
          {expiringSerials.length > 0 && (
            <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30" data-testid="dashboard-serial-alert">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center shrink-0">
                    <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      {expiringSerials.length} Kunden anrufen!
                    </h3>
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">Geräte mit ablaufender oder abgelaufener Garantie/Wartung</p>
                    <div className="mt-3 space-y-2">
                      {expiringSerials.slice(0, 3).map((serial) => (
                        <div key={serial.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 bg-white/60 dark:bg-black/20 rounded-lg p-2">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <Badge variant={serial.expiry_date < today ? "destructive" : "default"}>{formatDate(serial.expiry_date)}</Badge>
                            <span className="font-medium truncate max-w-full">{serial.customer_name}</span>
                            <span className="text-sm text-muted-foreground truncate max-w-full">{serial.product_name}</span>
                          </div>
                          {serial.customer_phone && (
                            <a href={`tel:${serial.customer_phone}`} className="flex items-center gap-1 text-sm text-primary hover:underline shrink-0">
                              <Phone className="w-3 h-3 shrink-0" />{serial.customer_phone}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                    {expiringSerials.length > 3 && (
                      <Button variant="link" asChild className="px-0 mt-2">
                        <Link to="/serial-numbers">Alle {expiringSerials.length} anzeigen<ArrowRight className="w-4 h-4 ml-1" /></Link>
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Anstehende Wiedervorlagen */}
          <Card data-testid="dashboard-followups-widget">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CalendarClock className="w-5 h-5 text-primary" />
                  Anstehende Wiedervorlagen
                </CardTitle>
                <CardDescription>Folgeaufgaben, nach Fälligkeit</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/tasks" data-testid="view-all-followups-btn">Alle<ArrowRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            </CardHeader>
            <CardContent>
              {followups.length > 0 ? (
                <div className="space-y-2.5">
                  {followups.slice(0, 6).map((t) => {
                    const due = t.due_date ? String(t.due_date).slice(0, 10) : null;
                    const overdue = due && due < today;
                    const isToday = due && due === today;
                    return (
                      <div key={t.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors" data-testid={`dashboard-followup-${t.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <button type="button" onClick={() => markFollowupDone(t.id)} title="Als erledigt abhaken" aria-label="Wiedervorlage als erledigt markieren"
                            className="w-8 h-8 rounded-lg border border-border bg-background hover:bg-success/10 hover:border-success text-muted-foreground hover:text-success flex items-center justify-center shrink-0 transition-colors"
                            data-testid={`dashboard-followup-done-${t.id}`}>
                            <Check className="w-4 h-4" />
                          </button>
                          <Link to={t.customer_id ? `/customers/${t.customer_id}` : "/tasks"} className="min-w-0 group/link">
                            <p className="font-medium truncate group-hover/link:underline">{t.customer_name || t.title}</p>
                            <p className="text-sm text-muted-foreground truncate">{t.title}</p>
                          </Link>
                        </div>
                        <Badge variant={overdue ? "destructive" : isToday ? "default" : "secondary"} className="shrink-0">
                          {due ? (overdue ? "Überfällig" : isToday ? "Heute" : formatDate(due)) : "Ohne Datum"}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state py-8" data-testid="dashboard-followups-empty">
                  <CalendarClock className="w-10 h-10 text-muted-foreground/40" />
                  <p className="text-muted-foreground mt-3 text-sm">Keine anstehenden Wiedervorlagen</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Aktuelle Serviceberichte */}
          <Card data-testid="dashboard-recent-reports">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="w-5 h-5 text-primary" />
                  Aktuelle Serviceberichte
                </CardTitle>
                <CardDescription>Die letzten {reportsCount > 5 ? 5 : reportsCount} Berichte</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/service-reports" data-testid="view-all-reports-btn">Alle<ArrowRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            </CardHeader>
            <CardContent>
              {recentReports.length > 0 ? (
                <div className="space-y-2.5">
                  {recentReports.map((report) => (
                    <Link key={report.id} to={`/service-reports/${report.id}/edit`}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors" data-testid={`dashboard-report-${report.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <FileText className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{report.title}</p>
                          <p className="text-sm text-muted-foreground truncate">{report.customer_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />{(report.work_hours || 0) + (report.travel_hours || 0)}h</Badge>
                        <span className="text-sm text-muted-foreground hidden sm:inline">{report.service_date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state py-10" data-testid="dashboard-reports-empty">
                  <FileText className="w-12 h-12 text-muted-foreground/40" />
                  <p className="text-muted-foreground mt-3">Noch keine Serviceberichte vorhanden</p>
                  <Button asChild className="mt-4"><Link to="/service-reports/new">Ersten Bericht erstellen</Link></Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Rechte Spalte */}
        <div className="space-y-6">
          {/* Mein Tag */}
          <Link to="/mein-tag" data-testid="dashboard-mein-tag-widget">
            <Card className="card-hover cursor-pointer border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-400 to-amber-300 flex items-center justify-center shrink-0 shadow-md shadow-orange-500/20">
                    <Sun className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold">Mein Tag</h3>
                      <span className="text-sm font-medium text-muted-foreground" data-testid="dashboard-mein-tag-count">{myDay.done}/{myDay.total} erledigt</span>
                    </div>
                    {myDay.total > 0 ? (
                      <Progress value={Math.round((myDay.done / myDay.total) * 100)} className="mt-2" data-testid="dashboard-mein-tag-progress" />
                    ) : (
                      <p className="text-sm text-muted-foreground mt-1">Heute noch nichts geplant.</p>
                    )}
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0" />
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Anstehende Termine */}
          <UpcomingEventsNotification hoursAhead={96} />

          {/* Aktivitäts-Feed */}
          <ActivityFeed items={activity} />
        </div>
      </div>
    </div>
  );
}
